import React, { useEffect } from 'react'
import { Redirect } from 'react-router-dom'

import Box from 'components_new/atoms/Box'

import SourceAbout from 'components_new/organisms/internal/sources/SourceAbout'
import SourceNavigation from 'components_new/organisms/internal/sources/SourceNavigation'
import SourceTablesColumn from 'components_new/organisms/internal/sources/SourceTablesColumn'

import PageLayout from 'components_new/templates/PageLayout'

import SourceTabContent from './SourceTabContent'
import { useSourceOptions } from './useSourceOptions'

import { useActiveSourceTable, useSourcesPageData } from 'helpers/hooks/Sources'
import { useSourcesActions } from 'redux/actions/DataPlatform/connections/Sources'
import { useActiveSource } from 'redux/hooks/Sources'
import { useParams } from 'utils/hooks'
import { ESourceTab, getSourceTabs } from 'utils/sources'

const SOURCES_HREF = '/data-platform/collect/sources'

const Source = () => {
  const { id, sourceType, tab } = useParams<{
    id: string
    sourceType: string
    tab: ESourceTab
  }>()

  const { accountLoading, allowed, fetched } = useSourcesPageData()
  const { activeSource, source } = useActiveSource(id)
  const { tryGetOneSource } = useSourcesActions()

  const { activeTable, setActiveTable, tables } =
    useActiveSourceTable(activeSource)
  const { dialogs, options } = useSourceOptions(activeSource, source)

  /**
   * Only the single-source endpoint returns the tables and the creator, and
   * fetching every source overwrites them — so this has to wait for that one to
   * land.
   */
  useEffect(() => {
    if (fetched) {
      tryGetOneSource(id)
    }
  }, [fetched, id])

  if (accountLoading) {
    return null
  }

  if (!allowed) {
    return <Redirect to="/" />
  }

  if (fetched && (!activeSource || !source)) {
    return <Redirect to={SOURCES_HREF} />
  }

  const tabs = source ? getSourceTabs(source.attributes) : []

  // A section can be reached by url that the source does not have — upload on
  // something that is not a file, for instance.
  if (source && !tabs.some((item) => item.tab === tab)) {
    return (
      <Redirect
        to={`/data-platform/collect/${sourceType}/${id}/${ESourceTab.DATA}`}
      />
    )
  }

  /**
   * Endpoints and upload describe the source itself rather than one of its
   * tables, so neither has a table to pick. Everywhere else the column stays
   * even for a single table — it says which table you are looking at.
   */
  const fullWidth = [ESourceTab.ENDPOINTS, ESourceTab.UPLOAD].includes(tab)

  /**
   * The data section is a grid and wants the whole area — edge to edge, full
   * height, scrolling inside itself. The remaining legacy sections still flow
   * down the page and need the padded feed layout.
   */
  const isDataSection = tab === ESourceTab.DATA

  return (
    <>
      <PageLayout
        content={
          isDataSection ? { disablePadding: true, fullWidth: true } : undefined
        }
        rightSidebar={{
          content: (
            <>
              <SourceNavigation sx={{ mb: 2 }} />
              <SourceAbout />
            </>
          ),
          size: 'medium'
        }}
        topbar={{
          breadcrumbs: [
            { title: 'Admin' },
            { href: SOURCES_HREF, title: 'Ansluten data' },
            ...(activeSource
              ? [{ title: activeSource.attributes.display_name }]
              : [])
          ],
          mobile: { goBack: true },
          options: options
        }}
        variant={isDataSection ? 'static' : 'feed'}
      >
        {activeSource && source ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: isDataSection ? 0 : 2,
              height: isDataSection ? '100%' : undefined,
              minHeight: 0
            }}
          >
            {fullWidth ? null : (
              <SourceTablesColumn
                activeTable={activeTable}
                onSelect={setActiveTable}
                tables={tables}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0
              }}
            >
              <SourceTabContent
                activeSource={activeSource}
                activeTable={activeTable}
                source={source}
                tab={tab}
              />
            </Box>
          </Box>
        ) : null}
      </PageLayout>

      {dialogs}
    </>
  )
}

export default Source
